import { useEffect, useState } from 'react';
import { Ban, CheckCircle2, PackageCheck, Pencil, ShieldCheck, Truck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  CHANNEL_RETURN_PROFILES,
  DISPOSITION_LABELS,
  ITEM_CONDITION_LABELS,
  LABEL_PAYER_LABELS,
  RECEIPT_MODE_LABELS,
  RESOLUTION_LABELS,
  RESOLUTION_STATUSES,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_VARIANTS,
  RETURN_CLOSED_STATUSES,
  RETURN_REASON_LABELS,
  RETURN_RECEIVABLE_STATUSES,
  RETURN_STATUS_LABELS,
  RETURN_STATUS_VARIANTS,
  returnDisplayCustomer,
  returnLineLabel,
  returnLinesValueCents,
  useUpdateReturn,
  type Resolution,
  type ResolutionStatus,
  type ReturnRow,
  type ReturnStatus,
} from '@/features/returns/hooks';
import {
  centsToInputDollars,
  formatCurrency,
  formatDate,
  formatRelative,
  inputDollarsToCents,
} from '@/lib/formatters';

interface ReturnDetailSheetProps {
  rma: ReturnRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  onEdit: (rma: ReturnRow) => void;
  onReceive: (rma: ReturnRow) => void;
}

/** One-line "what happens next" for the drawer header. */
function nextStep(r: ReturnRow): string | null {
  if (RETURN_CLOSED_STATUSES.includes(r.status)) return null;
  const venue = CHANNEL_RETURN_PROFILES[r.channel_kind].resolutionVenue;
  if (r.status === 'requested') return 'Review the request and authorize or reject it.';
  if (r.receipt_mode === 'ship_to_us' && RETURN_RECEIVABLE_STATUSES.includes(r.status) && r.status !== 'partially_received') {
    return r.resolution_status === 'pending'
      ? 'Waiting for the unit to arrive. Receive it here when it does.'
      : 'Resolution issued — still waiting for the unit to arrive.';
  }
  if (r.resolution_status === 'pending') return `Issue the ${RESOLUTION_LABELS[r.resolution].toLowerCase()} in ${venue}, then record it below.`;
  if (r.status === 'partially_received') return 'Some units are still outstanding. Receive the rest or close the RMA.';
  return 'Everything is settled — close the RMA.';
}

export function ReturnDetailSheet({ rma, open, onOpenChange, canManage, onEdit, onReceive }: ReturnDetailSheetProps) {
  const update = useUpdateReturn();

  const [resolution, setResolution] = useState<Resolution>('refund');
  const [resolutionStatus, setResolutionStatus] = useState<ResolutionStatus>('pending');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');

  useEffect(() => {
    if (!rma) return;
    setResolution(rma.resolution);
    setResolutionStatus(rma.resolution_status);
    const suggested = Math.max(0, returnLinesValueCents(rma) - rma.restocking_fee_cents);
    setAmount(centsToInputDollars(rma.refund_cents ?? (suggested || null)));
    setReference(rma.resolution_reference ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when the row itself changes
  }, [rma?.id, rma?.updated_at]);

  if (!rma) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent />
      </Sheet>
    );
  }

  const profile = CHANNEL_RETURN_PROFILES[rma.channel_kind];
  const isOpen = !RETURN_CLOSED_STATUSES.includes(rma.status);
  const canReceive = rma.receipt_mode === 'ship_to_us' && RETURN_RECEIVABLE_STATUSES.includes(rma.status);
  const hasReceipts = rma.return_receipt_lines.length > 0;
  const resolutionOptions = Array.from(new Set<Resolution>([...profile.resolutions, rma.resolution]));
  const step = nextStep(rma);

  const setStatus = async (status: ReturnStatus, message: string) => {
    try {
      await update.mutateAsync({ id: rma.id, status });
      toast.success(message);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the RMA.');
    }
  };

  const saveResolution = async () => {
    let cents: number | null;
    try {
      cents = inputDollarsToCents(amount);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check the amount.');
      return;
    }
    try {
      await update.mutateAsync({
        id: rma.id,
        resolution,
        resolution_status: resolutionStatus,
        refund_cents: cents,
        resolution_reference: reference.trim() || null,
      });
      toast.success('Resolution saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the resolution.');
    }
  };

  const busy = update.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{profile.label}</Badge>
            <Badge variant={RETURN_STATUS_VARIANTS[rma.status]}>{RETURN_STATUS_LABELS[rma.status]}</Badge>
            <Badge variant={RESOLUTION_STATUS_VARIANTS[rma.resolution_status]}>
              {RESOLUTION_LABELS[rma.resolution]}: {RESOLUTION_STATUS_LABELS[rma.resolution_status]}
            </Badge>
          </div>
          <SheetTitle>{rma.rma_number}</SheetTitle>
          <SheetDescription>
            Requested {formatDate(rma.requested_at)} · {formatRelative(rma.requested_at)}
            {(rma.sales_orders?.so_number || rma.external_order_ref) && (
              <> · order {rma.sales_orders?.so_number ?? rma.external_order_ref}</>
            )}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-6">
          {step && (
            <p className="rounded-md border border-atmosphere-dark/30 bg-atmosphere/10 px-3 py-2 text-sm">{step}</p>
          )}

          {canManage && isOpen && (
            <div className="flex flex-wrap gap-2">
              {rma.status === 'requested' && (
                <>
                  <Button size="sm" disabled={busy} onClick={() => setStatus('authorized', `${rma.rma_number} authorized`)}>
                    <ShieldCheck className="h-4 w-4" />
                    Authorize
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus('rejected', `${rma.rma_number} rejected`)}>
                    <XCircle className="h-4 w-4" />
                    Reject
                  </Button>
                </>
              )}
              {rma.status === 'authorized' && rma.receipt_mode === 'ship_to_us' && (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus('in_transit', `${rma.rma_number} marked in transit`)}>
                  <Truck className="h-4 w-4" />
                  Mark in transit
                </Button>
              )}
              {canReceive && (
                <Button size="sm" disabled={busy} onClick={() => onReceive(rma)}>
                  <PackageCheck className="h-4 w-4" />
                  Receive
                </Button>
              )}
              {rma.status !== 'requested' && rma.resolution_status !== 'pending' && (
                <Button size="sm" disabled={busy} onClick={() => setStatus('closed', `${rma.rma_number} closed`)}>
                  <CheckCircle2 className="h-4 w-4" />
                  Close RMA
                </Button>
              )}
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onEdit(rma)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              {!hasReceipts && rma.status !== 'requested' && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStatus('cancelled', `${rma.rma_number} cancelled`)}>
                  <Ban className="h-4 w-4" />
                  Cancel
                </Button>
              )}
            </div>
          )}

          <section className="space-y-2">
            <SectionTitle>Customer &amp; reason</SectionTitle>
            <div className="space-y-1 rounded-lg border border-border bg-card/40 p-3 text-sm">
              <p className="font-medium text-foreground">{returnDisplayCustomer(rma)}</p>
              {rma.customer_email && rma.customer_email !== returnDisplayCustomer(rma) && (
                <p className="text-muted-foreground">{rma.customer_email}</p>
              )}
              <p>
                <span className="text-muted-foreground">Reason: </span>
                {RETURN_REASON_LABELS[rma.reason_code]}
              </p>
              {rma.reason_detail && <p className="whitespace-pre-wrap text-muted-foreground">{rma.reason_detail}</p>}
              <p className="text-xs text-muted-foreground">
                {RECEIPT_MODE_LABELS[rma.receipt_mode]}
                {rma.external_return_id && (
                  <>
                    {' '}· {profile.label} return <span className="font-mono">{rma.external_return_id}</span>
                  </>
                )}
              </p>
            </div>
          </section>

          <section className="space-y-2">
            <SectionTitle>Items ({rma.return_lines.length})</SectionTitle>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Item</th>
                    <th className="px-2 py-2 text-right font-medium">Qty</th>
                    <th className="px-2 py-2 text-right font-medium">Recv'd</th>
                    <th className="px-3 py-2 text-right font-medium">Price paid</th>
                  </tr>
                </thead>
                <tbody>
                  {rma.return_lines.map((l) => (
                    <tr key={l.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="text-foreground">{returnLineLabel(l)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {l.items?.sku ?? l.sku ?? ''}
                          {!l.item_id && <span className="ml-1 text-fresh-salmon-dark">unmapped</span>}
                          {l.disposition && <> · plan: {DISPOSITION_LABELS[l.disposition]}</>}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{l.quantity}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{l.received_quantity}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(l.unit_price_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {hasReceipts && (
            <section className="space-y-2">
              <SectionTitle>Received</SectionTitle>
              <ul className="space-y-1.5 text-sm">
                {rma.return_receipt_lines.map((rr) => {
                  const line = rma.return_lines.find((l) => l.id === rr.return_line_id);
                  return (
                    <li key={rr.id} className="rounded-md border border-border px-3 py-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate">
                          {rr.quantity}× {line ? returnLineLabel(line) : 'Item'}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">{formatDate(rr.received_at)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {DISPOSITION_LABELS[rr.disposition]}
                        {rr.locations && <> → {rr.locations.name}</>}
                        {rr.condition && <> · {ITEM_CONDITION_LABELS[rr.condition]}</>}
                        {rr.lots && <> · lot {rr.lots.lot_number}</>}
                      </div>
                      {rr.notes && <div className="mt-0.5 text-xs text-muted-foreground">{rr.notes}</div>}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="space-y-2">
            <SectionTitle>Resolution</SectionTitle>
            <div className="space-y-3 rounded-lg border border-border bg-card/40 p-3">
              <p className="text-xs text-muted-foreground">Issued in {profile.resolutionVenue}.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={resolution} onValueChange={(v) => setResolution(v as Resolution)} disabled={!canManage}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {resolutionOptions.map((r) => (
                        <SelectItem key={r} value={r}>
                          {RESOLUTION_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select
                    value={resolutionStatus}
                    onValueChange={(v) => setResolutionStatus(v as ResolutionStatus)}
                    disabled={!canManage}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOLUTION_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {RESOLUTION_STATUS_LABELS[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-amount">Amount refunded / credited</Label>
                  <Input
                    id="rma-amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                    disabled={!canManage}
                  />
                  {rma.restocking_fee_cents > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Net of {formatCurrency(rma.restocking_fee_cents)} restocking fee
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-ref">Reference</Label>
                  <Input
                    id="rma-ref"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder={rma.channel_kind === 'b2b_portal' ? 'Credit memo #' : 'Refund id'}
                    className="font-mono text-xs"
                    disabled={!canManage}
                  />
                </div>
              </div>
              {rma.resolution_issued_at && (
                <p className="text-xs text-muted-foreground">Issued {formatDate(rma.resolution_issued_at)}</p>
              )}
              {canManage && (
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={saveResolution} disabled={busy}>
                    Save resolution
                  </Button>
                </div>
              )}
            </div>
          </section>

          {rma.receipt_mode === 'ship_to_us' && (rma.carrier || rma.tracking_number || rma.label_paid_by) && (
            <section className="space-y-2">
              <SectionTitle>Return shipment</SectionTitle>
              <div className="rounded-lg border border-border bg-card/40 p-3 text-sm">
                {rma.carrier && <span>{rma.carrier} </span>}
                {rma.tracking_number && <span className="font-mono text-xs">{rma.tracking_number}</span>}
                {rma.label_paid_by && (
                  <p className="text-xs text-muted-foreground">Label paid by {LABEL_PAYER_LABELS[rma.label_paid_by]}</p>
                )}
              </div>
            </section>
          )}

          {rma.notes && (
            <section className="space-y-2">
              <SectionTitle>Notes</SectionTitle>
              <p className="whitespace-pre-wrap rounded-lg border border-border bg-card/40 p-3 text-sm">{rma.notes}</p>
            </section>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</h3>;
}
